import { object, string } from 'yup';
import { BaseModel, CreateBaseModel } from '../base.model';
import { JsonObject } from '../../helpers';
import { TelemetryConfig, TelemetryType } from '.';
import { TelemetrySettings } from '@/lib/services/telemetry.service';
import { has } from 'lodash';
import { TelemetrySources } from './telemetry-sources';
import { TelemetrySyncStates } from './telemetry-sync-states';
import { IconType } from '@/components/icon/helpers';

export class TelemetryModel extends BaseModel {
  orgId: string;
  type: TelemetryType;
  typeLabel: string;
  status: string;
  statusMessage: string;
  connectorId: string;
  enabled: boolean;
  displayName: string;
  config: TelemetryConfig;
  telemetryTypes: TelemetrySources;
  syncState: TelemetrySyncStates;
  canTest: boolean;
  updatedAt: string;
  iconImage: string;
  icon?: IconType;

  schema = object({
    displayName: string().required('Name is required'),
    type: string().required('Type is required'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof TelemetryModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.type = (data.type as TelemetryType) || '';
    this.typeLabel = (data.typeLabel as string) || 'Unknown';
    this.status = (data.status as string) || '';
    this.statusMessage = (data.statusMessage as string) || '';
    this.connectorId = (data.connectorId as string) || '';
    this.enabled = !!data.enabled;
    this.displayName = (data.displayName as string) || '';
    this.config = (data.config as TelemetryConfig) || {};

    this.syncState = new TelemetrySyncStates(data.syncState as JsonObject);
    this.canTest = false;
    this.updatedAt = (data.updatedAt as string) || '';

    this.iconImage = '';
    this.icon = 'telemetry';

    if (has(data, 'telemetryTypes.userEvents.enabled')) {
      this.telemetryTypes = new TelemetrySources(data.telemetryTypes as JsonObject);
    } else {
      // legacy schema support
      const oldTypeConfig = data.telemetryTypes as Record<string, boolean>;
      const schemaVersion = (data as unknown as { schemaVersion: string }).schemaVersion || 'v1';

      this.telemetryTypes = {
        userEvents: {
          enabled: !!oldTypeConfig?.userEvents,
          schemaVersion,
          includeConversationData: true,
        },
        auditEvents: {
          enabled: !!oldTypeConfig?.auditEvents,
          schemaVersion: 'v1',
        },
      };
    }

    // legacy schema support for sync state
    if (has(data, 'lastSyncedAt') && !has(data, 'syncState')) {
      const {
        lastSyncedAt,
        lastSyncedUserEventId,
        lastSyncedEventTimestamp,
        consecutiveFailureCount,
      } = data;
      this.syncState.userEvents = {
        lastSyncedAt,
        lastSyncedEventId: lastSyncedUserEventId,
        lastSyncedEventTimestamp,
        consecutiveFailureCount,
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getDestinationUrl(_settings: TelemetrySettings): string {
    return '';
  }

  get createModel(): CreateBaseModel {
    return new CreateTelemetryModel(this);
  }

  get patchModel(): CreateBaseModel {
    return new PatchTelemetryModel(this);
  }
}

export class CreateTelemetryModel extends CreateBaseModel {
  connectorId?: string;
  enabled: boolean;
  displayName: string;
  config: Partial<TelemetryConfig>;
  telemetryTypes: TelemetrySources;
  type: TelemetryType;

  constructor(telemetry: TelemetryModel) {
    super();

    this.type = telemetry.type;
    this.connectorId = telemetry.connectorId;
    this.enabled = telemetry.enabled;
    this.displayName = telemetry.displayName;
    this.config = telemetry.config;
    this.telemetryTypes = telemetry.telemetryTypes;
  }
}

export class PatchTelemetryModel extends CreateBaseModel {
  connectorId?: string;
  enabled: boolean;
  displayName: string;
  config: Partial<TelemetryConfig>;
  telemetryTypes: TelemetrySources;

  constructor(telemetry: TelemetryModel) {
    super();

    this.connectorId = telemetry.connectorId;
    this.enabled = telemetry.enabled;
    this.displayName = telemetry.displayName;
    this.config = telemetry.config;
    this.telemetryTypes = telemetry.telemetryTypes;
  }
}
